import { useEffect, useState } from 'react';
import { getMiMeta, getHoy } from '../services/api';
import { CheckCircle2, Circle, PartyPopper } from 'lucide-react';

const pct = (a: number, m: number) => (m > 0 ? Math.min(Math.round((a / m) * 100), 100) : 0);
const barColor = (p: number) => (p >= 100 ? '#6b9b65' : p >= 60 ? '#1d3d6b' : p >= 30 ? '#f59e0b' : '#ef4444');
const money = (n: number) => `$${(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

function Ring({ value, label, sub, color }: { value: number; label: string; sub: string; color: string }) {
  const r = 40, circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(value, 100) / 100) * circ;
  return (
    <div className="flex flex-col items-center">
      <svg width={100} height={100} viewBox="0 0 100 100">
        <circle cx={50} cy={50} r={r} fill="none" stroke="#f3f4f6" strokeWidth={10} />
        <circle cx={50} cy={50} r={r} fill="none" stroke={color} strokeWidth={10}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          transform="rotate(-90 50 50)" />
        <text x="50" y="55" textAnchor="middle" fontSize={18} fontWeight="700" fill={color}>{value}%</text>
      </svg>
      <p className="text-xs font-medium text-gray-700 mt-1">{label}</p>
      <p className="text-[11px] text-gray-400">{sub}</p>
    </div>
  );
}

const METAS_DIARIAS_LABEL: Record<string, (n: number) => string> = {
  prospectos: n => `Conseguir ${n} prospecto${n === 1 ? '' : 's'}`,
  llamadas:   n => `Hacer ${n} llamada${n === 1 ? '' : 's'}`,
  citas:      n => `Agendar ${n} cita${n === 1 ? '' : 's'}`,
  iniciales:  n => `Hacer ${n} inicial${n === 1 ? '' : 'es'}`,
  cierre:     n => n === 1 ? 'Hacer un cierre' : `Hacer ${n} cierres`,
};
const METAS_DIARIAS_ORDEN = ['prospectos', 'llamadas', 'citas', 'iniciales', 'cierre'];

export default function Hoy() {
  const [meta, setMeta] = useState<any>(null);
  const [hoy, setHoy] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getMiMeta(), getHoy()])
      .then(([metaRes, hoyRes]) => { setMeta(metaRes.data); setHoy(hoyRes.data); })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-gray-400">Cargando...</div>;
  }

  const m = meta?.meta;
  const av = meta?.avance;
  const od = meta?.objetivosDerivados;

  return (
    <div className="space-y-5">
      <h2 className="text-2xl font-bold text-gray-800">Hoy</h2>

      {/* Avance de meta del mes */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-700">
            Avance de la meta {meta && !meta.esMetaIndividual && m && (
              <span className="text-xs font-normal text-gray-400">(meta general de la agencia)</span>
            )}
          </h3>
        </div>
        {!m ? (
          <p className="text-gray-400 text-sm">Aún no tienes una meta configurada este mes. Pídele a tu gerente que la defina.</p>
        ) : (
          <div className="flex flex-wrap gap-6 justify-around">
            <Ring value={pct(av.polizasTotales, m.metaPolizasTotales)} color={barColor(pct(av.polizasTotales, m.metaPolizasTotales))}
              label="Pólizas" sub={`${av.polizasTotales} / ${m.metaPolizasTotales}`} />
            <Ring value={pct(av.primaEmitida, m.primaEmitidaMeta)} color={barColor(pct(av.primaEmitida, m.primaEmitidaMeta))}
              label="Prima emitida" sub={`${money(av.primaEmitida)} / ${money(m.primaEmitidaMeta)}`} />
            <Ring value={pct(av.primaPagada, m.primaPagadaMeta)} color={barColor(pct(av.primaPagada, m.primaPagadaMeta))}
              label="Prima pagada" sub={`${money(av.primaPagada)} / ${money(m.primaPagadaMeta)}`} />
          </div>
        )}
      </div>

      {/* Objetivos de actividad derivados */}
      {od && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">Lo que necesitas este mes para llegar a tu meta</h3>
          {!od.basadoEnHistorico && (
            <p className="text-[11px] text-gray-400 mb-3">Aún no tienes suficiente historial — estos números usan supuestos conservadores y se ajustarán con tus propios resultados.</p>
          )}
          <div className="grid grid-cols-3 gap-3 mt-2">
            {[
              { label: 'Llamadas', value: od.llamadasNecesarias, hecho: av.llamadas },
              { label: 'Citas', value: od.citasNecesarias, hecho: av.citasIniciales },
              { label: 'Cierres', value: od.cierresNecesarios, hecho: av.citasCierre },
            ].map(o => (
              <div key={o.label} className="bg-gray-50 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-gray-800">{o.hecho} / {o.value}</p>
                <p className="text-[11px] text-gray-400 uppercase">{o.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Metas diarias */}
      {meta?.metasDiarias && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Metas diarias</h3>
            {METAS_DIARIAS_ORDEN.every(k => meta.metasDiarias[k]?.cumplidoHoy) && (
              <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                <PartyPopper size={14} /> ¡Día completo!
              </span>
            )}
          </div>
          <div className="divide-y divide-gray-100">
            {METAS_DIARIAS_ORDEN.map(key => {
              const md = meta.metasDiarias[key];
              if (!md) return null;
              return (
                <div key={key} className="flex items-center gap-3 py-2.5">
                  {md.cumplidoHoy
                    ? <CheckCircle2 size={22} className="text-green-500 flex-shrink-0" />
                    : <Circle size={22} className="text-gray-300 flex-shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">{METAS_DIARIAS_LABEL[key](md.metaDiaria)}</p>
                    <p className="text-xs text-gray-400">{md.vecesEsteMes} {md.vecesEsteMes === 1 ? 'vez' : 'veces'} este mes</p>
                  </div>
                </div>
              );
            })}
          </div>

          {Array.isArray(meta.semana) && (
            <>
              <div className="flex justify-between mt-4 pt-4 border-t border-gray-100">
                {meta.semana.map((d: any) => (
                  <div key={d.fecha} className="flex flex-col items-center gap-1">
                    {d.cumplido
                      ? <CheckCircle2 size={22} className="text-green-500" />
                      : d.esFinde
                        ? <Circle size={22} className="text-gray-200" strokeDasharray="3 3" />
                        : <Circle size={22} className={d.esHoy ? 'text-primary-400' : 'text-gray-300'} />}
                    <span className={`text-[11px] ${d.esHoy ? 'font-bold text-primary-600' : 'text-gray-400'}`}>{d.letra}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 text-center mt-2">Sábado y domingo son opcionales</p>
            </>
          )}
        </div>
      )}

      {/* Actividad de hoy */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Actividad de hoy</h3>
        <div className="grid grid-cols-4 gap-3 text-center">
          {[
            { label: 'Llamadas', value: hoy.actividadHoy.llamadasHoy },
            { label: 'Citas agendadas', value: hoy.actividadHoy.citasAgendadasHoy },
            { label: 'Realizadas', value: hoy.actividadHoy.citasCompletadasHoy },
            { label: 'No-show', value: hoy.actividadHoy.noShowHoy },
          ].map(a => (
            <div key={a.label} className="bg-gray-50 rounded-xl py-2">
              <p className="text-lg font-bold text-gray-800">{a.value}</p>
              <p className="text-[10px] text-gray-400 uppercase leading-tight">{a.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Citas de hoy */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Citas de hoy</h3>
        {hoy.citasHoy.length === 0 ? (
          <p className="text-gray-400 text-sm">No tienes citas agendadas para hoy.</p>
        ) : (
          <div className="space-y-2">
            {hoy.citasHoy.map((c: any) => (
              <div key={c.id} className="flex items-center justify-between border border-gray-100 rounded-xl p-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{c.titulo}</p>
                  <p className="text-xs text-gray-400">{c.cliente || c.prospecto || c.candidato || '—'} · {c.tipo}</p>
                </div>
                <span className="text-sm font-semibold text-gray-600">{formatTime(c.fechaHora)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lo importante hoy */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Lo importante hoy</h3>

        {hoy.cumpleañosHoy.length > 0 && (
          <div className="mb-3">
            {hoy.cumpleañosHoy.map((nombre: string) => (
              <div key={nombre} className="bg-pink-50 border border-pink-100 rounded-xl p-3 mb-2 text-sm text-gray-700">
                🎂 Cumpleaños de <b>{nombre}</b>
              </div>
            ))}
          </div>
        )}

        {hoy.renovacionesProximas.length === 0 && hoy.pagosPendientes.length === 0 && hoy.cumpleañosHoy.length === 0 ? (
          <p className="text-gray-400 text-sm">Nada urgente por el momento.</p>
        ) : (
          <div className="space-y-2">
            {hoy.renovacionesProximas.map((r: any) => (
              <div key={`r-${r.id}`} className="flex items-center justify-between border border-gray-100 rounded-xl p-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{r.cliente}</p>
                  <p className="text-xs text-gray-400">Renovación · {r.numeroPoliza} · {r.ramo}</p>
                </div>
                <span className="text-xs font-semibold text-amber-600">{formatDate(r.vigenciaFin)}</span>
              </div>
            ))}
            {hoy.pagosPendientes.map((p: any) => (
              <div key={`p-${p.id}`} className="flex items-center justify-between border border-gray-100 rounded-xl p-3">
                <div>
                  <p className="text-sm font-medium text-gray-800">{p.cliente}</p>
                  <p className="text-xs text-gray-400">{p.numeroPoliza} · {money(p.monto)}</p>
                </div>
                <span className={`text-xs font-semibold ${p.vencido ? 'text-red-600' : 'text-amber-600'}`}>
                  {p.vencido ? 'Vencido ' : ''}{formatDate(p.fechaVencimiento)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
